package br.gov.go.pge.permanenciaweb.site.components

import androidx.compose.runtime.Composable
import br.gov.go.pge.permanenciaweb.shared.EventDto
import br.gov.go.pge.permanenciaweb.shared.OccupantDto
import br.gov.go.pge.permanenciaweb.shared.SemSaidaRowDto
import br.gov.go.pge.permanenciaweb.site.IndustrialPalette
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.UserSelect
import com.varabyte.kobweb.compose.css.VerticalAlign
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.userSelect
import com.varabyte.kobweb.compose.ui.modifiers.verticalAlign
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.icons.ChevronDownIcon
import com.varabyte.kobweb.silk.components.icons.ChevronUpIcon
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.extendedByBase
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.dom.Button as HtmlButton
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Th

/*
 * Multi-column sort model shared by both tables. `List<Criterion<Field>>` is ordered by priority
 * (the first criterion decides first); an empty list means "keep the server order".
 */

internal enum class SortDirection { DESC, ASC }

internal data class Criterion<Field>(val field: Field, val direction: SortDirection)

/**
 * Sort rotation shared by the click handlers: desc → asc → none. A plain click replaces the whole
 * sort with the clicked column (cycling when it was the sole criterion); Shift+click adds it at
 * the end (lowest priority), cycles it in place, or drops it when the rotation is exhausted.
 */
internal fun <Field> toggledSort(current: List<Criterion<Field>>, field: Field, shift: Boolean): List<Criterion<Field>> {
    val index = current.indexOfFirst { it.field == field }

    if (!shift) {
        val sole = index == 0 && current.size == 1
        if (!sole) return listOf(Criterion(field, SortDirection.DESC))
        return current[0].direction.next()?.let { listOf(Criterion(field, it)) } ?: emptyList()
    }

    if (index < 0) return current + Criterion(field, SortDirection.DESC)
    val next = current[index].direction.next() ?: return current.filterIndexed { i, _ -> i != index }
    return current.mapIndexed { i, c -> if (i == index) Criterion(field, next) else c }
}

private fun SortDirection.next(): SortDirection? = when (this) {
    SortDirection.DESC -> SortDirection.ASC
    SortDirection.ASC -> null
}

/**
 * Text comparison for every sortable column: case-insensitive, blanks (null/empty/whitespace)
 * always last regardless of [direction], which only flips the order of actual values. Times are
 * fixed-width strings, so lexicographic order is also chronological order.
 */
internal fun compareText(a: String?, b: String?, direction: SortDirection): Int {
    val av = a?.takeIf { it.isNotBlank() }
    val bv = b?.takeIf { it.isNotBlank() }
    if (av == null || bv == null) {
        return when {
            av == null && bv == null -> 0
            av == null -> 1
            else -> -1
        }
    }
    val cmp = av.compareTo(bv, ignoreCase = true)
    return if (direction == SortDirection.ASC) cmp else -cmp
}

private fun <T, Field> Criterion<Field>.comparator(select: (T, Field) -> String?): Comparator<T> =
    Comparator { a, b -> compareText(select(a, field), select(b, field), direction) }

/**
 * Chained comparator for [criteria], or `null` for an empty list. Ties fall back to the next
 * criterion and, at the end, to the stable sort's server order.
 */
internal fun <T, Field> List<Criterion<Field>>.comparator(select: (T, Field) -> String?): Comparator<T>? =
    map { it.comparator(select) }.reduceOrNull { acc, next -> acc.then(next) }

/** Sorts by the criteria chain; no criteria keeps the list untouched (server order). */
internal fun <T, Field> List<T>.sortedWithCriteria(
    criteria: List<Criterion<Field>>,
    select: (T, Field) -> String?,
): List<T> = criteria.comparator(select)?.let { sortedWith(it) } ?: this

/** Roster columns sortable through [SortableTh]; the actions column stays fixed. */
internal enum class RosterSortField {
    NOME, TIPO, TELEFONE, SALA, ANDAR, UNIDADE, CARGO, ENTRADA, ALERTA;

    fun text(o: OccupantDto): String? = when (this) {
        NOME -> o.nome
        TIPO -> o.tipoAcesso
        TELEFONE -> o.telefone
        SALA -> o.sala
        ANDAR -> o.andar
        UNIDADE -> o.unidade
        CARGO -> o.cargo
        ENTRADA -> o.entrada
        ALERTA -> o.alertasTexto()
    }
}

/** Event columns sortable through [SortableTh]. */
internal enum class EventSortField {
    HORARIO, SENTIDO, PESSOA, TIPO, SETOR, ANDAR, SALA, UNIDADE, FONTE, DISPOSITIVO;

    fun text(e: EventDto): String? = when (this) {
        HORARIO -> e.horario
        SENTIDO -> e.sentido
        PESSOA -> e.pessoa
        TIPO -> e.tipoAcesso
        SETOR -> e.setor
        ANDAR -> e.andar
        SALA -> e.sala
        UNIDADE -> e.unidade
        FONTE -> e.fonteNome
        DISPOSITIVO -> e.dispositivo
    }
}

/** Sem saída columns sortable through [SortableTh]. */
internal enum class SemSaidaSortField {
    NOME, TIPO, CARGO, SETOR, SALA, ANDAR, UNIDADE, DIAS, ULTIMO_DIA;

    fun text(r: SemSaidaRowDto): String? = when (this) {
        NOME -> r.nome
        TIPO -> r.tipoAcesso
        CARGO -> r.cargo
        SETOR -> r.setor
        SALA -> r.sala
        ANDAR -> r.andar
        UNIDADE -> r.unidade
        // Counts need zero-padding, otherwise 10 sorts before 2.
        DIAS -> r.diasSemSaida.toString().padStart(2, '0')
        // The wire format is dd/MM/yyyy; yyyyMMdd is chronologically sortable.
        ULTIMO_DIA -> brDateSortKey(r.ultimoDia)
    }
}

private fun brDateSortKey(value: String): String {
    val parts = value.split('/')
    return if (parts.size == 3) "${parts[2]}${parts[1]}${parts[0]}" else value
}

/** The `<th>` itself is no longer interactive; the sort button inside owns cursor and focus. */
val SortableHeaderCellStyle = TableHeaderCellStyle.extendedByBase {
    Modifier
}

val SortableNumericHeaderCellStyle = TableNumericHeaderCellStyle.extendedByBase {
    Modifier
}

/**
 * The sort control: a real button that spans the label side of the header. Its UA styles are reset
 * so the header keeps the exact same typography, spacing and colors as before.
 */
val ColumnSortButtonStyle = CssStyle.base {
    Modifier
        .display(DisplayStyle.Flex)
        .alignItems(AlignItems.Center)
        .flexGrow(1f)
        .padding(0.cssRem)
        .margin(0.cssRem)
        .backgroundColor(Colors.Transparent)
        .border(0.cssRem)
        .cursor(Cursor.Pointer)
        .userSelect(UserSelect.None)
        .styleModifier {
            property("font", "inherit")
            property("color", "inherit")
            property("line-height", "inherit")
            property("letter-spacing", "inherit")
            property("text-align", "start")
            property("text-transform", "inherit")
            property("min-width", "0")
        }
}

val SortIndicatorStyle = CssStyle.base {
    Modifier
        .margin(left = 0.25.cssRem)
        .verticalAlign(VerticalAlign.Middle)
}

/**
 * Sortable table header: a real button sorts on click (Shift = multi-sort) or Enter/Space, with
 * the current direction exposed through `aria-sort` on the `<th>`. The chevron shows the direction;
 * the ordinal (1, 2, …) only appears once more than one criterion is active.
 *
 * The magnifier on the right opens the column filter popover. It is a sibling of the sort button,
 * not a descendant, so there is no interactive element inside another one.
 */
@Composable
internal fun <Field> SortableTh(
    label: String,
    field: Field,
    criteria: List<Criterion<Field>>,
    filter: ColumnFilter,
    filterId: String,
    options: List<String>?,
    numeric: Boolean = false,
    onSort: (Field, Boolean) -> Unit,
    onFilterChange: (ColumnFilter) -> Unit,
) {
    val index = criteria.indexOfFirst { it.field == field }
    val criterion = criteria.getOrNull(index)
    val style = if (numeric) SortableNumericHeaderCellStyle else SortableHeaderCellStyle
    val sortAriaLabel = buildString {
        append("Ordenar por $label")
        if (criterion != null) {
            append(if (criterion.direction == SortDirection.ASC) ", crescente" else ", decrescente")
            if (criteria.size > 1) append(", prioridade ${index + 1}")
        }
    }

    Th(
        attrs = style.toModifier().toAttrs {
            attr("scope", "col")
            attr(
                "aria-sort",
                when (criterion?.direction) {
                    SortDirection.ASC -> "ascending"
                    SortDirection.DESC -> "descending"
                    null -> "none"
                },
            )
        },
    ) {
        Div(attrs = ColumnHeaderStyle.toAttrs()) {
            HtmlButton(
                attrs = ColumnSortButtonStyle.toModifier().toAttrs {
                    attr("type", "button")
                    attr("data-sort-field", field.toString().lowercase())
                    attr("aria-label", sortAriaLabel)
                    onClick { evt -> onSort(field, evt.shiftKey) }
                },
            ) {
                Span(attrs = ColumnHeaderLabelStyle.toAttrs()) {
                    Text(label)
                    if (criterion != null) {
                        if (criterion.direction == SortDirection.ASC) {
                            ChevronUpIcon(SortIndicatorStyle.toModifier())
                        } else {
                            ChevronDownIcon(SortIndicatorStyle.toModifier())
                        }
                        if (criteria.size > 1) Text(" ${index + 1}")
                    }
                }
            }
            Div(attrs = ColumnFilterGroupStyle.toAttrs()) {
                if (filter.isActive) {
                    Span(
                        attrs = ColumnFilterCountStyle.toModifier()
                            .then(Modifier.color(IndustrialPalette.Steel))
                            .toAttrs { attr("aria-hidden", "true") },
                    ) {
                        Text("(${filter.activeCount})")
                    }
                }
                ColumnFilterButton(
                    filterId = filterId,
                    label = label,
                    filter = filter,
                    options = options,
                    onFilterChange = onFilterChange,
                )
            }
        }
    }
}
